import { nextTick, readonly, ref } from "vue";
import * as Sentry from "@sentry/vue";
import { useTranslation } from "@/language/translation";

const feedbackButtonGlowing = ref(false);
let feedback: ReturnType<typeof Sentry.getFeedback> | null = null;
const configuredFeedbackForms = new WeakSet<HTMLFormElement>();

export function moveOpenFeedbackForm(dialog: HTMLDialogElement | null) {
  const host = document.querySelector<HTMLElement>("#sentry-feedback");
  if (!host) return;

  if (dialog) {
    if (!host.shadowRoot?.querySelector("dialog.dialog[open], .success__position")) return;
    if (host.matches(":popover-open")) host.hidePopover();
    dialog.append(host);
    host.setAttribute("popover", "manual");
    host.showPopover();
  } else {
    if (host.matches(":popover-open")) host.hidePopover();
    host.removeAttribute("popover");
    document.body.append(host);
  }
}

function restoreFeedbackFormHost() {
  moveOpenFeedbackForm(null);
}

function configureOpenFeedbackForm() {
  const host = document.querySelector<HTMLElement>("#sentry-feedback");
  const dialog = document
    .querySelector(".SentryFeedbackButton")
    ?.closest<HTMLDialogElement>("dialog.lisa-dialog[open]");
  moveOpenFeedbackForm(dialog ?? null);
  const feedbackRoot = host?.shadowRoot;
  const messageInput = feedbackRoot?.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
  const form = messageInput?.form;

  messageInput?.focus();

  if (!form || configuredFeedbackForms.has(form)) {
    return;
  }

  form.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" || (!event.ctrlKey && !event.metaKey) || event.isComposing) {
      return;
    }

    event.preventDefault();
    form.requestSubmit();
  });
  configuredFeedbackForms.add(form);
}

Sentry.addEventProcessor((event) => {
  if (event.level === "error" && event.exception) {
    feedbackButtonGlowing.value = true;
    setTimeout(() => {
      feedbackButtonGlowing.value = false;
    }, 2000);
  }
  return event;
});

/**
 * A composable function to attach Sentry feedback to a specified HTML element.
 * This function initializes the Sentry feedback integration and binds it to the element
 * selected by the provided CSS selector.
 * @param {string} selector - The CSS selector of the HTML element to attach the feedback form to.
 * @returns {Object} An object containing the `attachToHTMLElement` function and a readonly
 * reference to the element.
 * @throws {Error} If the element with the specified selector is not found.
 * @example
 * const { attachToHTMLElement, element } = useSentryFeedback("#feedback-button");
 * attachToHTMLElement();
 * This will attach the Sentry feedback form to the button with ID "feedback-button".
 */
export function useSentryFeedback(selector: string) {
  const { t } = useTranslation();
  const element = ref(document.querySelector(selector));
  async function attachToHTMLElement() {
    await nextTick();
    element.value = document.querySelector(selector);
    if (!element.value) {
      throw new Error(`Element with selector "${selector}" not found.`);
    }
    feedback?.remove();
    feedback = feedback ?? Sentry.getFeedback();

    feedback?.attachTo(element.value, {
      formTitle: t("sentry.formTitle"),
      submitButtonLabel: t("sentry.submitButtonLabel"),
      cancelButtonLabel: t("global.cancel"),
      confirmButtonLabel: t("global.confirm"),
      addScreenshotButtonLabel: t("sentry.addScreenshotButtonLabel"),
      removeScreenshotButtonLabel: t("sentry.removeScreenshotButtonLabel"),
      nameLabel: t("sentry.nameLabel"),
      namePlaceholder: t("sentry.namePlaceholder"),
      emailLabel: t("sentry.emailLabel"),
      emailPlaceholder: t("sentry.emailPlaceholder"),
      messageLabel: t("sentry.messageLabel"),
      messagePlaceholder: t("sentry.messagePlaceholder"),
      successMessageText: t("sentry.successMessageText"),
      isRequiredLabel: t("sentry.isRequiredLabel"),
      highlightToolText: t("sentry.highlightToolText"),
      hideToolText: t("sentry.hideToolText"),
      removeHighlightText: t("sentry.removeHighlightText"),
      onFormOpen: () => configureOpenFeedbackForm(),
      onFormClose: restoreFeedbackFormHost,
      onFormSubmitted: restoreFeedbackFormHost,
    });
  }

  return {
    attachToHTMLElement,
    element: readonly(element),
    floatingButton: {
      isGlowing: feedbackButtonGlowing,
      glow: () => {
        if (!feedbackButtonGlowing.value) {
          feedbackButtonGlowing.value = true;
          setTimeout(() => {
            feedbackButtonGlowing.value = false;
          }, 2000);
        }
      },
    },
  };
}
